import { canonicalMetricId, humanDuration, networkSummary } from './asset-observability';
import { getActiveTimeZone } from './timezone';
import { customerMetricLabel, customerTemperatureLabel } from '@aot-aiops/shared';

export type ChartPoint = {
  windowStart?: string;
  windowEnd?: string;
  avg?: number | null;
  latest?: number | null;
};
export type ChartOverlay = {
  id: string;
  kind: 'anomaly' | 'incident' | 'event' | 'change' | 'maintenance';
  label: string;
  at: string;
  endAt?: string;
};

export function metricLabel(metric: string) {
  return customerMetricLabel(metric);
}

export function metricUnit(metric: string) {
  metric = canonicalMetricId(metric);
  if (metric.endsWith('.percent') || metric.includes('.utilization_percent.')) return 'percent';
  if (metric.endsWith('.bits_per_second')) return 'bits/second';
  if (metric.endsWith('.bytes_per_second')) return 'bytes/second';
  if (
    metric.endsWith('.bytes') ||
    metric.includes('.capacity_bytes.') ||
    metric.includes('.used_bytes.') ||
    metric.includes('.free_bytes.')
  )
    return 'bytes';
  if (metric.endsWith('.seconds')) return 'seconds';
  if (metric.startsWith('temperature.celsius.')) return 'celsius';
  return undefined;
}
export function humanBitsPerSecond(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—';
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  let scaled = Math.max(0, value);
  let unit = 0;
  while (scaled >= 1000 && unit < units.length - 1) {
    scaled /= 1000;
    unit++;
  }
  return `${scaled >= 100 ? scaled.toFixed(0) : scaled >= 10 ? scaled.toFixed(1) : scaled.toFixed(2)} ${units[unit]}`;
}
export function humanBytes(value: number | null | undefined, perSecond = false) {
  if (value == null || !Number.isFinite(value)) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let scaled = Math.max(0, value);
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit++;
  }
  return `${scaled >= 100 ? scaled.toFixed(0) : scaled >= 10 ? scaled.toFixed(1) : scaled.toFixed(2)} ${units[unit]}${perSecond ? '/s' : ''}`;
}
export function formatMetricValue(value: number | null | undefined, unit?: string) {
  if (value == null || !Number.isFinite(value)) return 'Not available';
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'bits/second') return humanBitsPerSecond(value);
  if (unit === 'bytes/second') return humanBytes(value, true);
  if (unit === 'bytes') return humanBytes(value);
  if (unit === 'celsius') return `${value.toFixed(value % 1 ? 1 : 0)}°C`;
  if (unit === 'milliseconds' || unit === 'ms') return `${value.toFixed(1)} ms`;
  if (unit === 'seconds') return humanDuration(value);
  return `${value.toFixed(value % 1 ? 1 : 0)}${unit ? ` ${displayMetricUnit(unit)}` : ''}`;
}

export function NetworkTrafficChart({
  history,
  range = '24h',
  timezone = getActiveTimeZone(),
  overlays = [],
  overlaysEnabled = true,
  onOverlaySelect,
}: {
  history: any[];
  range?: string;
  timezone?: string;
  overlays?: ChartOverlay[];
  overlaysEnabled?: boolean;
  onOverlaySelect?: (overlay: ChartOverlay) => void;
}) {
  const summary = networkSummary([], history);
  const merged = new Map<number, { at: number; rx: number | null; tx: number | null }>();
  const add = (points: any[], field: 'rx' | 'tx') => {
    for (const point of points) {
      const at = new Date(point.windowEnd ?? point.windowStart).getTime();
      const value = Number(point.avg ?? point.latest);
      if (!Number.isFinite(at) || !Number.isFinite(value)) continue;
      const current = merged.get(at) ?? { at, rx: null, tx: null };
      current[field] = value;
      merged.set(at, current);
    }
  };
  add(summary.receive.points, 'rx');
  add(summary.transmit.points, 'tx');
  const points = [...merged.values()].sort((left, right) => left.at - right.at);
  if (!points.length)
    return (
      <article className="history-chart observability-chart no-data">
        <strong>Network RX / TX</strong>
        <p>No samples in the selected range</p>
      </article>
    );
  const values = points
    .flatMap((point) => [point.rx, point.tx])
    .filter((value): value is number => value != null);
  const ceiling = Math.max(1, ...values);
  const start = points[0]!.at;
  const end = points.at(-1)!.at;
  const x = (at: number) => (end === start ? 150 : ((at - start) / (end - start)) * 300);
  const y = (value: number) => 76 - (value / ceiling) * 64;
  const expectedStep = range === '30d' ? 86_400_000 : range === '7d' ? 3_600_000 : 300_000;
  const lines = (field: 'rx' | 'tx') => {
    const segments: string[][] = [];
    let segment: string[] = [];
    points.forEach((point, index) => {
      const previous = index ? points[index - 1] : null;
      const gap =
        point[field] == null || (previous != null && point.at - previous.at > expectedStep * 2.5);
      if (gap && segment.length) {
        segments.push(segment);
        segment = [];
      }
      if (point[field] != null) segment.push(`${x(point.at)},${y(point[field]!)}`);
    });
    if (segment.length) segments.push(segment);
    return segments;
  };
  const tickCount = Math.min(5, Math.max(2, points.length));
  const ticks = Array.from(
    { length: tickCount },
    (_, index) => start + ((end - start) * index) / Math.max(1, tickCount - 1),
  );
  return (
    <article
      className="history-chart observability-chart network-traffic-chart"
      tabIndex={0}
      aria-label="Network receive and transmit time series"
    >
      <div>
        <strong>Network RX / TX</strong>
        <span>
          {humanBitsPerSecond(summary.receive.current)} in ·{' '}
          {humanBitsPerSecond(summary.transmit.current)} out
        </span>
      </div>
      <div className="network-chart-legend">
        <span className="receive">RX</span>
        <span className="transmit">TX</span>
      </div>
      <svg
        viewBox="0 0 300 96"
        preserveAspectRatio="none"
        role="img"
        aria-label="Network receive and transmit throughput"
      >
        <line x1="0" y1="76" x2="300" y2="76" />
        {overlaysEnabled &&
          overlays.map((overlay) => {
            const at = Date.parse(overlay.at);
            if (!Number.isFinite(at) || at < start || at > end) return null;
            const markerX = x(at);
            const endAt = overlay.endAt ? Math.min(end, Date.parse(overlay.endAt)) : at;
            return (
              <g
                key={overlay.id}
                className={`chart-evidence-overlay ${overlay.kind}`}
                role="button"
                tabIndex={0}
                aria-label={`${overlay.kind}: ${overlay.label}`}
                onClick={() => onOverlaySelect?.(overlay)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onOverlaySelect?.(overlay);
                }}
              >
                {endAt > at && (
                  <rect x={markerX} y="8" width={Math.max(2, x(endAt) - markerX)} height="70" />
                )}
                <line x1={markerX} y1="8" x2={markerX} y2="78" />
                <circle cx={markerX} cy="10" r="3">
                  <title>{overlay.label}</title>
                </circle>
              </g>
            );
          })}
        {lines('rx').map((line, index) => (
          <polyline key={`rx-${index}`} className="receive" points={line.join(' ')} />
        ))}
        {lines('tx').map((line, index) => (
          <polyline key={`tx-${index}`} className="transmit" points={line.join(' ')} />
        ))}
        {points.map((point) => (
          <circle
            key={point.at}
            cx={x(point.at)}
            cy={y(Math.max(point.rx ?? 0, point.tx ?? 0))}
            r="2"
          >
            <title>{`${new Date(point.at).toLocaleString(undefined, { timeZone: timezone })} · RX ${humanBitsPerSecond(point.rx)} · TX ${humanBitsPerSecond(point.tx)}`}</title>
          </circle>
        ))}
      </svg>
      <div className="chart-time-axis" aria-label={`Time shown in ${timezone}`}>
        {ticks.map((tick) => (
          <time key={tick} dateTime={new Date(tick).toISOString()}>
            {timeLabel(tick, range, timezone)}
          </time>
        ))}
      </div>
      <small>
        {timezone} · RX and TX share automatic bps/Kbps/Mbps/Gbps scaling · gaps remain missing
      </small>
    </article>
  );
}
export function interfaceBitsPerSecond(speedBps: number, utilization?: number | null) {
  if (
    !Number.isFinite(speedBps) ||
    speedBps <= 0 ||
    utilization == null ||
    !Number.isFinite(utilization)
  )
    return null;
  return Math.max(0, (speedBps * Math.min(100, utilization)) / 100);
}
export function temperatureSensorLabel(metric: string) {
  return customerTemperatureLabel(metric);
}
export function displayMetricUnit(unit?: string) {
  if (unit === 'celsius') return '°C';
  if (unit === 'percent') return '%';
  if (unit === 'percent_rh') return '% RH';
  if (unit === 'bits/second') return 'bps';
  if (unit === 'bytes/second') return 'B/s';
  return unit ?? '';
}
function pointValue(point: ChartPoint) {
  const value = point.avg ?? point.latest;
  return value == null || !Number.isFinite(Number(value)) ? null : Number(value);
}
function pointTime(point: ChartPoint) {
  const value = point.windowEnd ?? point.windowStart;
  const time = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(time) ? time : null;
}
function timeLabel(value: number, range: string, timezone: string) {
  const options: Intl.DateTimeFormatOptions =
    range === '7d' || range === '30d'
      ? { month: 'short', day: 'numeric', ...(range === '7d' ? { hour: 'numeric' } : {}) }
      : { hour: '2-digit', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: timezone }).format(value);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(value);
  }
}
export function TimeSeriesChart({
  metric,
  unit,
  points,
  lower,
  upper,
  range = '24h',
  timezone = getActiveTimeZone(),
  capability,
  overlays = [],
  overlaysEnabled = true,
  onOverlaySelect,
}: {
  metric: string;
  unit?: string;
  points: ChartPoint[];
  lower?: number;
  upper?: number;
  range?: string;
  timezone?: string;
  capability?: string;
  overlays?: ChartOverlay[];
  overlaysEnabled?: boolean;
  onOverlaySelect?: (overlay: ChartOverlay) => void;
}) {
  const samples = points.map((point) => ({
    point,
    value: pointValue(point),
    time: pointTime(point),
  }));
  const valid = samples.filter(
    (sample): sample is typeof sample & { value: number } => sample.value != null,
  );
  const label = metricLabel(metric);
  if (!valid.length) {
    const unsupported = ['NOT_SUPPORTED', 'NOT_REPORTED', 'NOT_AVAILABLE'].includes(
      capability ?? '',
    );
    return (
      <article className="history-chart observability-chart no-data">
        <strong>{label}</strong>
        <p>
          {unsupported
            ? 'Metric not supported for this asset'
            : 'No data available for this period'}
        </p>
      </article>
    );
  }
  const values = valid.map((sample) => sample.value);
  const floor = Math.min(...values, lower ?? Infinity);
  const ceiling = Math.max(...values, upper ?? -Infinity);
  const span = Math.max(1, ceiling - floor);
  const dated = valid.filter((sample) => sample.time != null);
  const actualStart = dated.length ? Math.min(...dated.map((sample) => sample.time!)) : 0;
  const end = dated.length
    ? Math.max(...dated.map((sample) => sample.time!))
    : Math.max(1, samples.length - 1);
  const rangeMs: Record<string, number> = {
    '1h': 3_600_000,
    '6h': 21_600_000,
    '24h': 86_400_000,
    '7d': 604_800_000,
    '30d': 2_592_000_000,
  };
  const start =
    dated.length && rangeMs[range] ? Math.min(actualStart, end - rangeMs[range]) : actualStart;
  const x = (sample: (typeof samples)[number], index: number) =>
    sample.time == null || end === start
      ? (index / Math.max(1, samples.length - 1)) * 300
      : ((sample.time - start) / (end - start)) * 300;
  const y = (value: number) => 76 - ((value - floor) / span) * 64;
  const expectedStep = range === '30d' ? 86_400_000 : range === '7d' ? 3_600_000 : 300_000;
  const segments: string[][] = [];
  let segment: string[] = [];
  samples.forEach((sample, index) => {
    const previous = index ? samples[index - 1] : null;
    const gap =
      sample.value == null ||
      (sample.time != null &&
        previous?.time != null &&
        sample.time - previous.time > expectedStep * 2.5);
    if (gap && segment.length) {
      segments.push(segment);
      segment = [];
    }
    if (sample.value != null) segment.push(`${x(sample, index)},${y(sample.value)}`);
  });
  if (segment.length) segments.push(segment);
  const band =
    lower != null && upper != null
      ? { y: y(upper), height: Math.max(2, y(lower) - y(upper)) }
      : null;
  const current = values.at(-1)!;
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  const maximum = Math.max(...values);
  const tickCount = Math.min(5, Math.max(2, dated.length));
  const ticks = Array.from(
    { length: tickCount },
    (_, index) => start + ((end - start) * index) / Math.max(1, tickCount - 1),
  );
  return (
    <article
      className="history-chart observability-chart"
      tabIndex={0}
      aria-label={`${label} time series`}
    >
      <div>
        <strong>{label}</strong>
        <span>{formatMetricValue(current, unit)}</span>
      </div>
      <div className="chart-summary">
        <small>
          Current <b>{formatMetricValue(current, unit)}</b>
        </small>
        <small>
          Average <b>{formatMetricValue(average, unit)}</b>
        </small>
        <small>
          Maximum <b>{formatMetricValue(maximum, unit)}</b>
        </small>
      </div>
      <svg
        viewBox="0 0 300 96"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label}, current ${formatMetricValue(current, unit)}, average ${formatMetricValue(average, unit)}, maximum ${formatMetricValue(maximum, unit)}`}
      >
        <line x1="0" y1="76" x2="300" y2="76" />
        {band && (
          <rect className="expected-range" x="0" y={band.y} width="300" height={band.height} />
        )}
        {overlaysEnabled &&
          overlays.map((overlay) => {
            const at = Date.parse(overlay.at);
            if (!Number.isFinite(at) || at < start || at > end) return null;
            const markerX = end === start ? 150 : ((at - start) / (end - start)) * 300;
            const endAt = overlay.endAt ? Math.min(end, Date.parse(overlay.endAt)) : at;
            const markerEnd = end === start ? markerX : ((endAt - start) / (end - start)) * 300;
            return (
              <g
                key={overlay.id}
                className={`chart-evidence-overlay ${overlay.kind}`}
                role="button"
                tabIndex={0}
                aria-label={`${overlay.kind}: ${overlay.label}`}
                onClick={() => onOverlaySelect?.(overlay)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onOverlaySelect?.(overlay);
                }}
              >
                {endAt > at && (
                  <rect x={markerX} y="8" width={Math.max(2, markerEnd - markerX)} height="70" />
                )}
                <line x1={markerX} y1="8" x2={markerX} y2="78" />
                <circle cx={markerX} cy="10" r="3">
                  <title>{overlay.label}</title>
                </circle>
              </g>
            );
          })}
        {segments.map((line, index) => (
          <polyline key={index} points={line.join(' ')} />
        ))}
        {valid.map((sample, index) => (
          <circle key={index} cx={x(sample, samples.indexOf(sample))} cy={y(sample.value)} r="2">
            <title>
              {sample.time == null
                ? ''
                : new Date(sample.time).toLocaleString(undefined, { timeZone: timezone })}{' '}
              · {label} · {formatMetricValue(sample.value, unit)}
            </title>
          </circle>
        ))}
      </svg>
      <div className="chart-time-axis" aria-label={`Time shown in ${timezone}`}>
        {ticks.map((tick) => (
          <time key={tick} dateTime={new Date(tick).toISOString()}>
            {timeLabel(tick, range, timezone)}
          </time>
        ))}
      </div>
      <small>{timezone} · gaps indicate missing samples</small>
    </article>
  );
}
